import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  PlusIcon,
  XMarkIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  UserIcon,
  EnvelopeIcon,
  TrashIcon,
  EyeIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

// Service type options
const SERVICE_TYPES = [
  { value: 'setup_fee', label: 'Website Setup Fee', color: '#3b82f6' },
  { value: 'monthly_hosting', label: 'Monthly Hosting', color: '#10b981' },
  { value: 'one_time_project', label: 'One-Time Project', color: '#8b5cf6' },
  { value: 'recurring_service', label: 'Recurring Service', color: '#f59e0b' },
  { value: 'extra_edits', label: 'Extra Edits', color: '#06b6d4' },
  { value: 'other', label: 'Other', color: '#6b7280' }
];

const PAYMENT_METHODS = [
  { value: 'square', label: 'Square' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'cash_app', label: 'Cash App' },
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' }
];

const WebInvoicesTab = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');

  const [invoiceForm, setInvoiceForm] = useState({
    client_name: '',
    client_email: '',
    service_type: 'setup_fee',
    description: '',
    amount: '',
    deposit_amount: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    is_recurring: false,
    recurring_interval: 'monthly',
    payment_method: '',
    notes: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_type: 'payment',
    payment_method: 'square',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchAnalytics();
  }, [statusFilter, serviceFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (serviceFilter) params.service_type = serviceFilter;
      
      const res = await api.get('/api/web-invoices', { params });
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/api/web-invoices/analytics/summary');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.client_name || !invoiceForm.amount) {
      toast.warning('Please fill in client name and amount');
      return;
    }

    try {
      await api.post('/api/web-invoices', {
        ...invoiceForm,
        amount: parseFloat(invoiceForm.amount),
        deposit_amount: invoiceForm.deposit_amount ? parseFloat(invoiceForm.deposit_amount) : 0
      });
      toast.success('Invoice created successfully');
      setShowInvoiceModal(false);
      resetInvoiceForm();
      fetchInvoices();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount) {
      toast.warning('Please enter payment amount');
      return;
    }

    try {
      await api.post(`/api/web-invoices/${selectedInvoice.id}/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      toast.success('Payment added successfully');
      setShowPaymentModal(false);
      resetPaymentForm();
      fetchInvoices();
      fetchAnalytics();
      
      // Refresh selected invoice if detail modal is open
      if (showDetailModal) {
        fetchInvoiceDetail(selectedInvoice.id);
      }
    } catch (error) {
      toast.error('Failed to add payment');
    }
  };

  const fetchInvoiceDetail = async (id) => {
    try {
      const res = await api.get(`/api/web-invoices/${id}`);
      setSelectedInvoice(res.data);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await api.delete(`/api/web-invoices/${id}`);
      toast.success('Invoice deleted');
      setShowDetailModal(false);
      fetchInvoices();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    const remaining = parseFloat(invoice.amount) - parseFloat(invoice.amount_paid || 0);
    
    try {
      await api.post(`/api/web-invoices/${invoice.id}/payments`, {
        amount: remaining,
        payment_type: 'payment',
        payment_method: invoice.payment_method || 'other',
        payment_date: new Date().toISOString().split('T')[0],
        notes: 'Marked as paid'
      });
      toast.success('Invoice marked as paid');
      fetchInvoices();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      client_name: '',
      client_email: '',
      service_type: 'setup_fee',
      description: '',
      amount: '',
      deposit_amount: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      is_recurring: false,
      recurring_interval: 'monthly',
      payment_method: '',
      notes: ''
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      payment_type: 'payment',
      payment_method: 'square',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { bg: '#d1fae5', color: '#065f46', icon: CheckCircleIcon },
      partial: { bg: '#fef3c7', color: '#92400e', icon: ClockIcon },
      unpaid: { bg: '#fee2e2', color: '#991b1b', icon: ExclamationCircleIcon },
      overdue: { bg: '#fecaca', color: '#991b1b', icon: ExclamationCircleIcon }
    };
    const style = styles[status] || styles.unpaid;
    const Icon = style.icon;
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        background: style.bg,
        color: style.color,
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500,
        textTransform: 'capitalize'
      }}>
        <Icon style={{ width: 14, height: 14 }} />
        {status}
      </span>
    );
  };

  const getServiceTypeBadge = (type) => {
    const service = SERVICE_TYPES.find(s => s.value === type) || SERVICE_TYPES[SERVICE_TYPES.length - 1];
    return (
      <span style={{
        padding: '2px 8px',
        background: `${service.color}15`,
        color: service.color,
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500
      }}>
        {service.label}
      </span>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Analytics Summary */}
      {analytics && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <div style={{
              padding: 20,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: 12,
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, opacity: 0.9 }}>
                <CurrencyDollarIcon style={{ width: 20, height: 20 }} />
                <span style={{ fontSize: 13 }}>Total Invoiced</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{formatCurrency(analytics.summary?.total_invoiced)}</div>
            </div>

            <div style={{
              padding: 20,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: 12,
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, opacity: 0.9 }}>
                <CheckCircleIcon style={{ width: 20, height: 20 }} />
                <span style={{ fontSize: 13 }}>Collected</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{formatCurrency(analytics.summary?.total_collected)}</div>
            </div>

            <div style={{
              padding: 20,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: 12,
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, opacity: 0.9 }}>
                <ClockIcon style={{ width: 20, height: 20 }} />
                <span style={{ fontSize: 13 }}>Outstanding</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{formatCurrency(analytics.summary?.total_outstanding)}</div>
            </div>

            <div style={{
              padding: 20,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: 12,
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, opacity: 0.9 }}>
                <ArrowTrendingUpIcon style={{ width: 20, height: 20 }} />
                <span style={{ fontSize: 13 }}>MRR</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{formatCurrency(analytics.mrr)}</div>
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ fontSize: 13, color: '#4a5568' }}>Paid: {analytics.summary?.paid_count || 0}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
              <span style={{ fontSize: 13, color: '#4a5568' }}>Partial: {analytics.summary?.partial_count || 0}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
              <span style={{ fontSize: 13, color: '#4a5568' }}>Unpaid: {analytics.summary?.unpaid_count || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14 }}
          >
            <option value="">All Status</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="unpaid">Unpaid</option>
            <option value="overdue">Overdue</option>
          </select>

          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14 }}
          >
            <option value="">All Service Types</option>
            {SERVICE_TYPES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <button
          onClick={() => setShowInvoiceModal(true)}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Invoice
        </button>
      </div>

      {/* Invoices List */}
      <div style={{ display: 'grid', gap: 12 }}>
        {invoices.map(invoice => {
          const remaining = parseFloat(invoice.amount) - parseFloat(invoice.amount_paid || 0);
          const percentPaid = (parseFloat(invoice.amount_paid || 0) / parseFloat(invoice.amount)) * 100;

          return (
            <div
              key={invoice.id}
              style={{
                padding: 16,
                background: 'white',
                borderRadius: 8,
                border: '1px solid #e2e8f0'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <h4 style={{ fontWeight: 600, fontSize: 15, margin: 0 }}>{invoice.client_name}</h4>
                    {getStatusBadge(invoice.status)}
                    {getServiceTypeBadge(invoice.service_type)}
                    {invoice.is_recurring && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#dbeafe',
                        color: '#1e40af',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500
                      }}>
                        Recurring
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#4a5568', marginBottom: 8 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <DocumentTextIcon style={{ width: 14, height: 14 }} />
                      {invoice.invoice_number}
                    </span>
                    {invoice.client_email && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <EnvelopeIcon style={{ width: 14, height: 14 }} />
                        {invoice.client_email}
                      </span>
                    )}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CalendarIcon style={{ width: 14, height: 14 }} />
                      {formatDate(invoice.invoice_date)}
                    </span>
                  </div>

                  {invoice.description && (
                    <p style={{ fontSize: 13, color: '#718096', margin: 0 }}>{invoice.description}</p>
                  )}

                  {/* Payment Progress */}
                  {invoice.status !== 'paid' && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#718096' }}>
                          {formatCurrency(invoice.amount_paid)} of {formatCurrency(invoice.amount)}
                        </span>
                        <span style={{ color: '#10b981', fontWeight: 500 }}>
                          {remaining > 0 ? `${formatCurrency(remaining)} remaining` : 'Paid'}
                        </span>
                      </div>
                      <div style={{
                        height: 6,
                        background: '#e2e8f0',
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(percentPaid, 100)}%`,
                          background: percentPaid >= 100 ? '#10b981' : '#3b82f6',
                          borderRadius: 3,
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right', marginLeft: 24 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1f2937' }}>
                    {formatCurrency(invoice.amount)}
                  </div>
                  {invoice.deposit_amount > 0 && (
                    <div style={{ fontSize: 12, color: '#718096' }}>
                      Deposit: {formatCurrency(invoice.deposit_amount)}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8
              }}>
                <button
                  onClick={() => {
                    fetchInvoiceDetail(invoice.id);
                    setShowDetailModal(true);
                  }}
                  style={{
                    padding: '6px 12px',
                    background: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <EyeIcon style={{ width: 14, height: 14 }} />
                  View
                </button>

                {invoice.status !== 'paid' && (
                  <>
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setPaymentForm({
                          ...paymentForm,
                          amount: remaining.toString()
                        });
                        setShowPaymentModal(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <BanknotesIcon style={{ width: 14, height: 14 }} />
                      Add Payment
                    </button>

                    <button
                      onClick={() => handleMarkAsPaid(invoice)}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <CheckCircleIcon style={{ width: 14, height: 14 }} />
                      Mark Paid
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {invoices.length === 0 && !loading && (
          <div style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
            <BanknotesIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
            <p>No invoices yet. Click "Add Invoice" to create your first one!</p>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showInvoiceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            width: '100%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 24,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BanknotesIcon style={{ width: 24, height: 24, color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Create Invoice</h3>
                  <p style={{ fontSize: 13, color: '#718096', margin: 0 }}>Add a new manual invoice</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowInvoiceModal(false);
                  resetInvoiceForm();
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              {/* Client Info */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Client Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Client Name *</label>
                    <input
                      type="text"
                      value={invoiceForm.client_name}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, client_name: e.target.value })}
                      placeholder="Business name"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Email</label>
                    <input
                      type="email"
                      value={invoiceForm.client_email}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, client_email: e.target.value })}
                      placeholder="client@example.com"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Service Details</h4>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Service Type *</label>
                  <select
                    value={invoiceForm.service_type}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, service_type: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  >
                    {SERVICE_TYPES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Description</label>
                  <textarea
                    value={invoiceForm.description}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                    placeholder="Service description..."
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minHeight: 60, resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Amount */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Amount</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Total Amount *</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#718096' }}>$</span>
                      <input
                        type="number"
                        value={invoiceForm.amount}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        style={{ width: '100%', padding: '10px 12px 10px 24px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Deposit Required</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#718096' }}>$</span>
                      <input
                        type="number"
                        value={invoiceForm.deposit_amount}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, deposit_amount: e.target.value })}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        style={{ width: '100%', padding: '10px 12px 10px 24px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Invoice Date</label>
                    <input
                      type="date"
                      value={invoiceForm.invoice_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Due Date</label>
                    <input
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                  </div>
                </div>
              </div>

              {/* Recurring */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={invoiceForm.is_recurring}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, is_recurring: e.target.checked })}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>This is a recurring invoice</span>
                </label>

                {invoiceForm.is_recurring && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Recurring Interval</label>
                    <select
                      value={invoiceForm.recurring_interval}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, recurring_interval: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Payment Method & Notes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Payment Method</label>
                  <select
                    value={invoiceForm.payment_method}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, payment_method: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  >
                    <option value="">Select method...</option>
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Notes</label>
                  <input
                    type="text"
                    value={invoiceForm.notes}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                    placeholder="Internal notes..."
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
              </div>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => {
                  setShowInvoiceModal(false);
                  resetInvoiceForm();
                }}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateInvoice}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Create Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1003,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            width: '100%',
            maxWidth: 450
          }}>
            <div style={{
              padding: 24,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Add Payment</h3>
                <p style={{ fontSize: 13, color: '#718096', margin: '4px 0 0 0' }}>
                  for {selectedInvoice.client_name} - {selectedInvoice.invoice_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  resetPaymentForm();
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Payment Amount *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#718096' }}>$</span>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{ width: '100%', padding: '10px 12px 10px 24px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Payment Type</label>
                <select
                  value={paymentForm.payment_type}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                >
                  <option value="deposit">Deposit</option>
                  <option value="payment">Payment</option>
                  <option value="refund">Refund</option>
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Payment Method</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                >
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Notes</label>
                <input
                  type="text"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Payment notes..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                />
              </div>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  resetPaymentForm();
                }}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddPayment}
                style={{
                  padding: '10px 24px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Add Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            width: '100%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 24,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <h3 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{selectedInvoice.invoice_number}</h3>
                  {getStatusBadge(selectedInvoice.status)}
                </div>
                <p style={{ fontSize: 14, color: '#4a5568', margin: 0 }}>{selectedInvoice.client_name}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              {/* Invoice Summary */}
              <div style={{
                padding: 16,
                background: '#f7fafc',
                borderRadius: 8,
                marginBottom: 24
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>Total Amount</div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{formatCurrency(selectedInvoice.amount)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>Amount Paid</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{formatCurrency(selectedInvoice.amount_paid)}</div>
                  </div>
                </div>

                {selectedInvoice.status !== 'paid' && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>Remaining Balance</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>
                      {formatCurrency(parseFloat(selectedInvoice.amount) - parseFloat(selectedInvoice.amount_paid || 0))}
                    </div>
                  </div>
                )}
              </div>

              {/* Details */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Details</h4>
                <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#718096' }}>Service Type:</span>
                    <span>{getServiceTypeBadge(selectedInvoice.service_type)}</span>
                  </div>
                  {selectedInvoice.client_email && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#718096' }}>Email:</span>
                      <span>{selectedInvoice.client_email}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#718096' }}>Invoice Date:</span>
                    <span>{formatDate(selectedInvoice.invoice_date)}</span>
                  </div>
                  {selectedInvoice.due_date && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#718096' }}>Due Date:</span>
                      <span>{formatDate(selectedInvoice.due_date)}</span>
                    </div>
                  )}
                  {selectedInvoice.is_recurring && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#718096' }}>Recurring:</span>
                      <span style={{ textTransform: 'capitalize' }}>{selectedInvoice.recurring_interval}</span>
                    </div>
                  )}
                </div>
                {selectedInvoice.description && (
                  <div style={{ marginTop: 12, padding: 12, background: '#f7fafc', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>Description</div>
                    <p style={{ fontSize: 14, margin: 0 }}>{selectedInvoice.description}</p>
                  </div>
                )}
              </div>

              {/* Payment History */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', margin: 0 }}>Payment History</h4>
                  {selectedInvoice.status !== 'paid' && (
                    <button
                      onClick={() => {
                        setPaymentForm({
                          ...paymentForm,
                          amount: (parseFloat(selectedInvoice.amount) - parseFloat(selectedInvoice.amount_paid || 0)).toString()
                        });
                        setShowPaymentModal(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <PlusIcon style={{ width: 14, height: 14 }} />
                      Add Payment
                    </button>
                  )}
                </div>

                {selectedInvoice.payments?.length > 0 ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {selectedInvoice.payments.map(payment => (
                      <div key={payment.id} style={{
                        padding: 12,
                        background: '#f7fafc',
                        borderRadius: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{formatCurrency(payment.amount)}</div>
                          <div style={{ fontSize: 12, color: '#718096' }}>
                            {formatDate(payment.payment_date)} • {payment.payment_method} • {payment.payment_type}
                          </div>
                          {payment.notes && (
                            <div style={{ fontSize: 12, color: '#4a5568', marginTop: 4 }}>{payment.notes}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: 14, color: '#718096', textAlign: 'center', padding: 20 }}>No payments recorded</p>
                )}
              </div>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                style={{
                  padding: '10px 16px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <TrashIcon style={{ width: 16, height: 16 }} />
                Delete Invoice
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebInvoicesTab;
